import { useCallback, useMemo } from 'react'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_SMS_VOLUME } from 'src/graphql/smsAnalytics.graphql'
import {
  TrendChartView,
  type TrendBucket,
} from 'src/pages/AnalyticsPage/components/TrendChart'
import {
  buildTrendSeriesFromPalette,
  useAutoBucket,
  useRefetchOnKeyChange,
} from 'src/pages/AnalyticsPage/components/trendChartHelpers'

import type { SMSDirection, SMSLogFiltersInput } from 'types/graphql'

const INFO_TEXT =
  'SMS volume over time, bucketed by day, week, or month. One line per direction (Inbound + Outbound). The bucket adapts to your selected date range; you can override it with the toggle.'

// Visual palette + display order for the two direction lines. Greens for
// outbound (the "active" direction recruiters send), blues for inbound.
const DIRECTION_PALETTE = [
  { id: 'OUTBOUND' as SMSDirection, label: 'Outbound', color: '#86EFAC' },
  { id: 'INBOUND' as SMSDirection, label: 'Inbound', color: '#93C5FD' },
]

interface SMSVolumePointShape {
  periodStart: string
  count: number
}
interface SMSVolumeDirectionSeriesShape {
  direction: SMSDirection
  points: ReadonlyArray<SMSVolumePointShape>
}
interface SMSVolumeShape {
  bucket: TrendBucket
  series: ReadonlyArray<SMSVolumeDirectionSeriesShape>
}

export interface SMSVolumeContainerProps {
  filters: SMSLogFiltersInput
  /**
   * Bumped by the page-level Refresh button to trigger a manual refetch.
   * Mount value is 0 — the initial useQuery already fetches, so the first
   * change to this prop (i.e. ≥ 1) is what drives a refetch.
   */
  refreshKey: number
}

const SMSVolumeContainer = ({
  filters,
  refreshKey,
}: SMSVolumeContainerProps) => {
  const startMs = filters.dateRange?.gte
    ? new Date(filters.dateRange.gte).getTime()
    : 0
  const endMs = filters.dateRange?.lte
    ? new Date(filters.dateRange.lte).getTime()
    : Date.now()
  const [bucket, setBucket] = useAutoBucket(startMs, endMs)

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_SMS_VOLUME, {
    variables: { input: { filters, bucket } },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  useRefetchOnKeyChange(refetch, refreshKey)

  const volume = data?.smsVolume as SMSVolumeShape | undefined

  const trendData = useMemo(() => {
    if (!volume) return undefined
    const series = buildTrendSeriesFromPalette(
      DIRECTION_PALETTE,
      volume.series,
      (s) => s.direction
    )
    return { bucket: volume.bucket, series }
  }, [volume])

  return (
    <TrendChartView
      title="SMS Volume"
      info={INFO_TEXT}
      data={trendData}
      loading={loading}
      bucket={bucket}
      onBucketChange={setBucket}
      tooltipUnit="message"
      emptyMessage="No SMS in this period"
    />
  )
}

export default SMSVolumeContainer
